import { Head, router } from '@inertiajs/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { CommunityOptionType, PaginationType, TaxPayerTypeFiltersType, TaxPayerTypeType } from './types.ts'
import Form from './form.tsx'
import {Button} from "@/components/ui/button.tsx";
import {Pencil, Plus, Search, Trash2} from "lucide-react";
import {Card} from "@/components/ui/card.tsx";
import {Input} from "@/components/ui/input.tsx";
import Pagination from "@/components/pagination.tsx";

interface IndexProps {
  tax_payer_types: TaxPayerTypeType[]
  filters: TaxPayerTypeFiltersType
  pagination: PaginationType
  communities?: CommunityOptionType[]
  can_create?: boolean
  can_update?: boolean
  can_destroy?: boolean
  can_select_community?: boolean
}

export default function Index({
  tax_payer_types,
  filters,
  pagination,
  communities = [],
  can_create = false,
  can_update = false,
  can_destroy = false,
  can_select_community = false,
}: IndexProps) {
  const hasInitialized = useRef(false)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [editingTaxPayerType, setEditingTaxPayerType] = useState<TaxPayerTypeType | null>(null)
  const [term, setTerm] = useState(filters.term)
  const emptyTaxPayerType: TaxPayerTypeType = {
    id: "",
    name: "",
    community_id: "",
    community_name: "",
    code: "",
    require_nif: false
  }
  const query = useMemo(() => ({ term }), [term])

  useEffect(() => {
    setTerm(filters.term)
  }, [filters.term])

  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true
      return
    }

    if (term === filters.term) {
      return
    }

    const timeoutId = setTimeout(() => {
      router.get("/admin/tax_payer_types", { ...query, page: 1 }, {
        preserveState: true,
        preserveScroll: true,
        replace: true
      })
    }, 350)

    return () => clearTimeout(timeoutId)
  }, [query, term, filters.term])

  const handleDestroy = (taxPayerType: TaxPayerTypeType) => {
    if (!window.confirm(`Supprimer le type "${taxPayerType.name}" ?`)) {
      return
    }

    router.delete(`/admin/tax_payer_types/${taxPayerType.id}`, {
      preserveScroll: true,
    })
  }

  return (
    <>
      <Head title="Type de contribuables" />
      <div className="p-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-foreground">Les types de contribuable</h2>
            <p className="text-muted-foreground">Gestion des différents types de contribuable</p>
          </div>
          {can_create && (
          <Button className="gap-2" onClick={() => setIsCreateModalOpen(true)}>
              <Plus className="h-4 w-4" />
              Ajouter un type
          </Button>
          )}
        </div>

        <Card className="mb-6 border-border bg-card p-6">


          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                  placeholder="Rechercher un type de contribuable par nom ou code..."
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  className="pl-10"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
              <tr className="border-b border-border text-left text-sm font-medium text-muted-foreground">
                <th className="pb-3 pr-4">Code</th>
                <th className="pb-3 pr-4">Nom</th>
                <th className="pb-3 pr-4">Commune</th>
                <th className="pb-3 pr-4">NIF requis</th>
                {(can_update || can_destroy) && <th className="pb-3 text-right">Actions</th>}
              </tr>
              </thead>
              <tbody>
              {tax_payer_types.length > 0 ? tax_payer_types.map((type) => (
                  <tr key={type.id} className="border-b border-border text-sm">
                    <td className="py-3 pr-4 font-medium text-foreground">{type.code}</td>
                    <td className="py-3 pr-4 text-foreground">{type.name}</td>
                    <td className="py-3 pr-4 text-muted-foreground">{type.community_name || "-"}</td>
                    <td className="py-3 pr-4 text-muted-foreground">{type.require_nif ? "Oui" : "Non"}</td>
                    {(can_update || can_destroy) && (
                      <td className="py-3 text-right">
                        <div className="flex justify-end gap-2">
                          {can_update && (
                            <Button variant="ghost" size="icon-sm" onClick={() => setEditingTaxPayerType(type)} aria-label={`Modifier ${type.name}`}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          {can_destroy && (
                            <Button variant="ghost" size="icon-sm" onClick={() => handleDestroy(type)} aria-label={`Supprimer ${type.name}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
              )) : (
                <tr>
                  <td className="py-6 text-center text-sm text-muted-foreground" colSpan={can_update || can_destroy ? 5 : 4}>
                    Aucun type de contribuable trouvé
                  </td>
                </tr>
              )}
              </tbody>
            </table>
          </div>
        </Card>
        <Pagination
          currentPage={pagination.current_page}
          totalPages={pagination.total_pages}
          basePath="/admin/tax_payer_types"
          query={query}
        />

      </div>

      {isCreateModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/10 px-4"
          onClick={() => setIsCreateModalOpen(false)}
        >
          <div
            className="w-full max-w-2xl rounded-lg bg-background  p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold">Ajouter un type de contribuable</h3>
              <button
                type="button"
                onClick={() => setIsCreateModalOpen(false)}
                className="rounded-md border border-gray-300 px-3 py-1 text-sm"
              >
                Fermer
              </button>
            </div>

              <Form
                className={"bg-background"}
              tax_payer_type={emptyTaxPayerType}
              communities={communities}
              can_select_community={can_select_community}
              method="post"
              action="/admin/tax_payer_types"
              onSuccess={() => setIsCreateModalOpen(false)}
              submitText="Créer un type de contribuable"
            />
          </div>
        </div>
      )}

      {editingTaxPayerType && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/10 px-4"
          onClick={() => setEditingTaxPayerType(null)}
        >
          <div
            className="w-full max-w-2xl rounded-lg bg-background p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold">Modifier un type de contribuable</h3>
              <button
                type="button"
                onClick={() => setEditingTaxPayerType(null)}
                className="rounded-md border border-gray-300 px-3 py-1 text-sm"
              >
                Fermer
              </button>
            </div>

            <Form
              className={"bg-background"}
              tax_payer_type={editingTaxPayerType}
              method="patch"
              action={`/admin/tax_payer_types/${editingTaxPayerType.id}`}
              onSuccess={() => setEditingTaxPayerType(null)}
              submitText="Mettre à jour le type"
            />
          </div>
        </div>
      )}
    </>
  )
}
